import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

type Shortcut = { keys: string; description: string };

const SHORTCUTS: Shortcut[] = [
  { keys: 'g then n', description: 'Go to News' },
  { keys: 'g then l', description: 'Go to Library' },
  { keys: 'g then c', description: 'Go to Collection' },
  { keys: 'g then s', description: 'Go to Stores' },
  { keys: 'g then m', description: 'Go to Community' },
  { keys: 'g then e', description: 'Go to Learn' },
  { keys: 'g then p', description: 'Go to Profile' },
  { keys: '?', description: 'Show this help' },
  { keys: 'Esc', description: 'Close dialogs / cancel' },
];

const TAB_KEYS: Record<string, string> = {
  n: 'news',
  l: 'library',
  c: 'collection',
  s: 'stores',
  m: 'community',
  e: 'learn',
  p: 'profile',
};

function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName.toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable;
}

/**
 * Sprint E — global keyboard shortcuts.
 * Two-key chord `g <tab>` jumps tabs via the hash router.
 * `?` opens a shortcut cheatsheet.
 */
export function KeyboardShortcuts() {
  const [showHelp, setShowHelp] = useState(false);
  const awaitingG = useRef(false);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      // ? — open help
      if (e.key === '?' || (e.key === '/' && e.shiftKey)) {
        e.preventDefault();
        setShowHelp(true);
        return;
      }

      if (awaitingG.current) {
        awaitingG.current = false;
        if (timer.current) window.clearTimeout(timer.current);
        const tab = TAB_KEYS[e.key.toLowerCase()];
        if (tab) {
          e.preventDefault();
          window.location.hash = `#${tab}`;
        }
        return;
      }

      if (e.key === 'g') {
        awaitingG.current = true;
        timer.current = window.setTimeout(() => {
          awaitingG.current = false;
        }, 1200);
      }
    };

    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, []);

  return (
    <Dialog open={showHelp} onOpenChange={setShowHelp}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-heading">Keyboard shortcuts</DialogTitle>
        </DialogHeader>
        <ul className="space-y-2 text-sm font-body">
          {SHORTCUTS.map((s) => (
            <li key={s.keys} className="flex items-center justify-between gap-4">
              <span className="text-muted-foreground">{s.description}</span>
              <kbd className="rounded border border-border bg-muted px-2 py-0.5 text-xs text-foreground">
                {s.keys}
              </kbd>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
